import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ErrorSummary, Field } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { BrandLockup } from '@/components/forge/BrandLockup'
import { Panel } from '@/components/forge/Panel'
import { useConfirmEmail, useSetPassword } from '@/features/users/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/**
 * The two screens an emailed link lands on.
 *
 * ── Why they exist, and why they exist now ───────────────────────────────
 * The API has been sending people here since before either page did. Inviting a
 * user mails `{frontend}/app/set-password?token=…`; changing your own address
 * mails `{frontend}/app/confirm-email?token=…`. Neither route was registered, so
 * both links landed on the app's not-found — an invitation that cannot be
 * accepted, and an address change that cannot complete.
 *
 * ── They are PUBLIC and live under /app anyway ──────────────────────────
 * The path is the server's choice, not this router's: `settings.frontend_base_url`
 * plus `/app/set-password` is what goes in the mail, and a link already sent
 * cannot be moved. So they are registered under `/app` but OUTSIDE the
 * `RequireAuth` guard — nobody arriving here has a session, and bouncing them to
 * sign in would ask for the password they are on their way to set.
 *
 * Both pages therefore sit beside the router's guard rather than inside it, and
 * neither renders the app shell: no nav rail, no scope tree, no top bar, because
 * there is no session to draw any of them from.
 *
 * ── One token, two journeys, one screen ─────────────────────────────────
 * `POST /users/set-password` serves the invitation's first password AND the
 * forgot-password reset — the server issues the same `set_password` token type
 * for both and tells them apart by whether a password was already stored. So
 * this is deliberately not two screens with a shared form: it is one screen,
 * and the copy is written to be true of either arrival.
 *
 * ── Nothing is revealed before the token is spent ───────────────────────
 * The token carries a user id and the server does not expose what it decodes to,
 * which is right: a page that greeted somebody by name before they proved
 * anything would confirm an address to whoever holds the link. So neither page
 * says who it is for. `confirm-email` reports the address only AFTER the server
 * has applied the change, because by then it is telling the account holder
 * something they already know.
 */

/** The frame both share, and the sign-in screen's. Also the MCP consent screen's. */
export function TokenFrame({ heading, intro, children }) {
  return (
    <div className="flex min-h-screen flex-col bg-page">
      <header className="border-b border-hairline bg-card">
        <div className="mx-auto flex min-h-14 w-full max-w-[1240px] items-center px-14 py-2 max-xl:px-8 max-lg:px-5">
          <Link to="/" className="flex items-center">
            <BrandLockup size="md" />
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-start justify-center px-5 py-14 max-lg:py-8">
        <div className="w-full max-w-[27rem]">
          <h1 className="m-0 text-display text-ink">{heading}</h1>
          <p className="mt-3 text-body leading-[1.6] text-ink-dim">{intro}</p>
          <Panel className="mt-7 p-6 max-lg:p-4.5">{children}</Panel>
        </div>
      </main>
    </div>
  )
}

/** A link with no token at all — someone pasted half of it, or opened the page directly. */
function MissingToken({ children }) {
  return (
    <p role="alert" className="m-0 rounded-sm border border-hairline stripe-red bg-card p-3.5 text-body-sm text-on-red">
      {children}
    </p>
  )
}

/**
 * The server's floor is one character. That is not a policy, it is an absence of
 * one, and shipping it as the client's rule would let somebody set a one-letter
 * password on an industrial control platform.
 *
 * So the form asks for twelve, and the 72 ceiling is bcrypt's — the server
 * enforces it, and a field that accepts what the API will reject is a form that
 * wastes a round trip. Recorded in BACKEND-GAPS.md: a password policy belongs on
 * the server, where it cannot be skipped by anything that is not this form.
 */
const PASSWORD_MIN_LENGTH = 12
const PASSWORD_MAX_LENGTH = 72

export function SetPasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  useDocumentTitle(t('setPassword.meta.title'), t('setPassword.meta.description'))

  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const setPasswordMutation = useSetPassword()

  const errors = {}
  if (!password) errors.password = t('setPassword.fields.password.required')
  else if (password.length < PASSWORD_MIN_LENGTH) {
    errors.password = t('setPassword.fields.password.tooShort', { count: PASSWORD_MIN_LENGTH })
  }
  /* Checked here and nowhere else — the API takes one password and has no idea a
     second field existed. Its whole job is catching a typo in something nobody
     can read back. */
  if (confirm !== password) errors.confirm = t('setPassword.fields.confirm.mismatch')

  /* An expired or already-spent token comes back keyed `token`, which is a
     different recovery from a rejected password: ask for a new link, do not
     retype. */
  const tokenRejected = Boolean(setPasswordMutation.error?.fields?.token)

  function handleSubmit(event) {
    event.preventDefault()
    setSubmitted(true)
    if (Object.keys(errors).length) return

    setPasswordMutation.mutate(
      { token, password },
      /* Straight to sign-in. There is no session to start from here — the server
         sets the password and issues nothing — so the next step is the login
         screen, and sending them there beats a success page with a link on it. */
      { onSuccess: () => navigate('/login?reason=passwordSet', { replace: true }) },
    )
  }

  if (!token) {
    return (
      <TokenFrame heading={t('setPassword.heading')} intro={t('setPassword.intro')}>
        <MissingToken>{t('setPassword.errors.noToken')}</MissingToken>
      </TokenFrame>
    )
  }

  return (
    <TokenFrame heading={t('setPassword.heading')} intro={t('setPassword.intro')}>
      {/* noValidate for the reason the sign-in form gives: the browser's own
          bubbles are unstyled, untranslated, and pre-empt the summary. */}
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4.5">
        {submitted && Object.keys(errors).length ? (
          <ErrorSummary
            id="set-password-errors"
            title={t('errors.summaryTitle', { count: Object.keys(errors).length })}
            errors={errors}
            className="-mb-1"
          />
        ) : null}

        {setPasswordMutation.isError ? (
          <p role="alert" className="m-0 rounded-sm border border-hairline stripe-red bg-card p-3.5 text-body-sm text-on-red">
            {tokenRejected
              ? t('setPassword.errors.tokenRejected')
              : t('common:errors.unknown')}
          </p>
        ) : null}

        <Field
          id="password"
          type="password"
          label={t('setPassword.fields.password.label')}
          hint={t('setPassword.fields.password.hint', { count: PASSWORD_MIN_LENGTH })}
          error={submitted ? errors.password : undefined}
          required
          value={password}
          maxLength={PASSWORD_MAX_LENGTH}
          onChange={(event) => setPassword(event.target.value)}
          // `new-password` on both, which is what tells a password manager to
          // offer to generate one rather than to fill the old one in.
          autoComplete="new-password"
        />

        <Field
          id="confirm"
          type="password"
          label={t('setPassword.fields.confirm.label')}
          error={submitted ? errors.confirm : undefined}
          required
          value={confirm}
          maxLength={PASSWORD_MAX_LENGTH}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
        />

        <ForgeButton type="submit" variant="primary" disabled={setPasswordMutation.isPending}>
          {setPasswordMutation.isPending
            ? t('setPassword.submitting')
            : t('setPassword.submit')}
        </ForgeButton>

        <p className="m-0 text-center text-micro text-ink-mute">{t('security.note')}</p>
      </form>
    </TokenFrame>
  )
}

/**
 * Confirm a changed email address.
 *
 * ── It does NOT confirm on mount ────────────────────────────────────────
 * A GET-shaped side effect fired from a `useEffect` is the pattern that makes an
 * email scanner, a link preview or a prefetching proxy spend somebody's token
 * before they ever see the page. The server accepts a POST here precisely so
 * that cannot happen, and firing it unprompted would hand the protection back.
 *
 * So there is a button. One click, and the token is spent by the person who
 * meant to spend it.
 */
export function ConfirmEmailPage() {
  const { t } = useTranslation(['auth', 'common'])
  useDocumentTitle(t('confirmEmail.meta.title'), t('confirmEmail.meta.description'))

  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const confirm = useConfirmEmail()

  const tokenRejected = Boolean(confirm.error?.fields?.token)
  const addressTaken = Boolean(confirm.error?.fields?.email)

  if (!token) {
    return (
      <TokenFrame heading={t('confirmEmail.heading')} intro={t('confirmEmail.intro')}>
        <MissingToken>{t('confirmEmail.errors.noToken')}</MissingToken>
      </TokenFrame>
    )
  }

  if (confirm.isSuccess) {
    return (
      <TokenFrame heading={t('confirmEmail.doneHeading')} intro={t('confirmEmail.doneIntro')}>
        <div className="flex flex-col gap-4">
          {/* Named only now. The server has applied the change, so this is
              telling the account holder something they already know rather than
              confirming an address to whoever held the link. */}
          {confirm.data?.email ? (
            <p className="m-0 text-body-sm text-ink">
              {t('confirmEmail.nowSignInWith', { email: confirm.data.email })}
            </p>
          ) : null}
          <ForgeButton asChild variant="primary">
            <Link to="/login">{t('confirmEmail.toSignIn')}</Link>
          </ForgeButton>
        </div>
      </TokenFrame>
    )
  }

  return (
    <TokenFrame heading={t('confirmEmail.heading')} intro={t('confirmEmail.intro')}>
      <div className="flex flex-col gap-4.5">
        {confirm.isError ? (
          <p role="alert" className="m-0 rounded-sm border border-hairline stripe-red bg-card p-3.5 text-body-sm text-on-red">
            {tokenRejected
              ? t('confirmEmail.errors.tokenRejected')
              : addressTaken
                ? t('confirmEmail.errors.addressTaken')
                : t('common:errors.unknown')}
          </p>
        ) : null}

        <ForgeButton
          variant="primary"
          disabled={confirm.isPending}
          onClick={() => confirm.mutate({ token })}
        >
          {confirm.isPending ? t('confirmEmail.submitting') : t('confirmEmail.submit')}
        </ForgeButton>

        {/* Until this is pressed the account still signs in with the OLD
            address. Worth saying: the profile form has been showing the new one
            since the request was made, which implies otherwise. */}
        <p className="m-0 text-body-sm text-ink-dim">{t('confirmEmail.pendingNote')}</p>
      </div>
    </TokenFrame>
  )
}
